import React, { useState, useCallback, useEffect } from 'react';
import { 
  ReactFlow, 
  Controls, 
  Background, 
  useNodesState, 
  useEdgesState, 
  Handle, 
  Position,
  MarkerType
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { Plus, User, Link as LinkIcon, X, Search, Copy, Check } from 'react-feather';
import { API_URL, fetchWithAuth, getImageUrl } from '../../api'; // Ajusta la ruta según tu proyecto

// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
// 1. CONFIGURACIÓN DEL LAYOUT (DAGRE)
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
const nodeWidth = 240;
const nodeHeight = 120; // Un poco más alto para que quepan los botones

const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // 'TB' = Top to Bottom (Vertical). Si prefieres horizontal usa 'LR'
  dagreGraph.setGraph({ rankdir: 'TB', ranksep: 80, nodesep: 50 }); 

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
// 2. COMPONENTE DE NODO PERSONALIZADO (LA TARJETA)
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
const TeamNode = ({ data }) => {
  const [copied, setCopied] = useState(false);
  const isMe = data.isMe;

  const hasLink = Boolean(data.encoded_info)

  console.log(data);

  const handleCopyLink = async (e) => {
  e.stopPropagation();

  if (!hasLink) return;

  const refCode = data.encoded_info || data.userId;
  const link = `${window.location.origin}/event/${data.eventId}?ref=${refCode}`;



  navigator.clipboard.writeText(link)
    .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000)
    })
    .catch((err) => {
        console.error("Error al copiar:", err);
    });
  };

  return (
    <div className={`relative w-[220px] rounded-xl border transition-all duration-300 group overflow-hidden
      ${isMe 
        ? 'bg-gradient-to-b from-indigo-900/80 to-black/80 border-indigo-500/50 shadow-[0_0_20px_rgba(99,102,241,0.2)]' 
        : 'bg-[#1a1a1a]/90 border-white/10 hover:border-white/30 hover:shadow-lg'}
    `}>
      <Handle type="target" position={Position.Top} className="!bg-white/40 !w-2 !h-2 !rounded-full !-top-1" />
      
      <div className="p-3">
        <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-full border-2 overflow-hidden flex items-center justify-center shrink-0
                ${isMe ? 'border-indigo-400' : 'border-white/10 bg-gray-800'}`}>
                {data.image ? (
                    <img src={getImageUrl(data.image)} alt="profile" className="w-full h-full object-cover" />
                ) : (
                    <User size={18} className="text-gray-400" />
                )}
            </div>
            
            <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold truncate ${isMe ? 'text-white' : 'text-gray-200'}`}>
                    {data.name || data.username}
                </p>
                <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded
                        ${data.depth === 0 ? 'bg-purple-500/20 text-purple-300' : 
                          data.depth === 1 ? 'bg-blue-500/20 text-blue-300' : 
                          'bg-gray-700/50 text-gray-400'}`}>
                        {data.role}
                    </span>
                    {isMe && <span className="text-[10px] bg-indigo-500 text-white px-1 rounded font-bold">TÚ</span>}
                </div>
            </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-white/5 gap-2">
            {/* BOTÓN COPIAR LINK */}
            <button
                onClick={handleCopyLink}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-bold transition-all
                    ${!hasLink 
                        ? 'bg-white/5 text-zinc-600 cursor-not-allowed border border-transparent' // Estado: Sin Link
                        : copied 
                            ? 'bg-green-500 text-white' // Estado: Copiado
                            : 'bg-indigo-500/10 hover:bg-indigo-500 text-indigo-400 hover:text-white border border-indigo-500/20' // Estado: Normal
                    }
                `}
            >
                {copied ? <Check size={12}/> : <Copy size={12}/>}
                {copied ? 'COPIADO' : !hasLink ? 'SIN LINK' : 'LINK'}
            </button>

            {/* Botón Agregar Equipo */}
            {data.canAdd && (
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        data.onAddSubordinate(data.id);
                    }}
                    className="flex items-center justify-center p-1.5 rounded bg-white/10 hover:bg-white text-white hover:text-black transition-all"
                    title="Añadir al equipo"
                >
                    <Plus size={14} />
                </button>
            )}

            {/* Botón Eliminar */}
            {data.canRemove && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`¿Seguro que deseas retirar a @${data.username}?`)) {
                            data.onRemove(data.username);
                        }
                    }}
                    className="flex items-center justify-center p-1.5 rounded bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white transition-all"
                >
                    <X size={14}/>
                </button>
            )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-white/40 !w-2 !h-2 !rounded-full !-bottom-1" />
    </div>
  );
};

const nodeTypes = { teamNode: TeamNode };

// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
// 3. COMPONENTE PRINCIPAL
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
const EventHierarchy = ({ eventId, currentUserId, isCreator }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);

  // Estados del Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [targetParentId, setTargetParentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isAdding, setIsAdding] = useState(false);

  const [targetParentDepth, setTargetParentDepth] = useState(0);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);

  const loadGraph = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_URL}/events/${eventId}/team-hierarchy`);
      const rawNodes = response || [];

      // Detectar mi nivel
      const myNode = rawNodes.find(n => String(n.user_id) === String(currentUserId));
      const myDepth = myNode ? myNode.depth : (isCreator ? -1 : 999); 

      // Mapear Nodos
      const flowNodes = rawNodes.map(n => {
        const isMe = String(n.user_id) === String(currentUserId);
        
        // Reglas de Permisos:
        // 1. Admin (isCreator) puede agregar en niveles 0 y 1 (para crear 1 y 2).
        // 2. Coordinadores pueden agregar debajo de ellos mismos.
        let canAdd = false;
        if (isCreator) {
            canAdd = n.depth < 4; // Admin puede crear ramas hasta nivel 2 (para tener 3 niveles total)
        } else {
            // Solo puedo agregar si es mi nodo o uno inferior, Y no excede profundidad
            canAdd = (n.depth >= myDepth) && (n.depth < 4) && (isMe || n.depth > myDepth);
        }

        // Lógica de canRemove
        // 1. Verificamos si tiene hijos en el grafo actual
        const hasChildren = rawNodes.some(child => child.parent_id === n.id);

        let canRemove = false;
        if(!isMe && !hasChildren) { // Nunca me puedo borrar a mi mismo y se bloquea si tiene hijos
            if (isCreator) {
                canRemove = true; // Admin borra a cualquiera (sin hijos)
            } else if (myNode && n.parent_id === myNode.id) {
                canRemove = true; // Yo puedo borrar a mis directos (sin hijos)
            }
        }

        return {
          id: n.id.toString(),
          type: 'teamNode',
          data: { 
              id: n.id,
              userId: n.user_id,
              eventId: eventId,
              name: n.name,
              username: n.username,
              role: n.role_label, // Ej: "Cabeza", "Promotor"
              image: n.profile_image,
              depth: n.depth,
              isMe: isMe, 
              canRemove: canRemove,
              canAdd: canAdd,
              encoded_info: n.encoded_info,
              onAddSubordinate: (parentId, parentDepth) => {
                const isRootAdmin = isCreator && isMe && !n.parent_id;
                openAddModal(isRootAdmin ? null : parentId, isRootAdmin ? 0: n.depth);
              },
              onRemove: (username) => handleRemoveUser(username)
          },
          position: { x: 0, y: 0 }
        };
      });

      // Mapear Conexiones (Edges)
      const flowEdges = rawNodes
        .filter(n => n.parent_id)
        .map(n => ({
          id: `e${n.parent_id}-${n.id}`,
          source: n.parent_id.toString(),
          target: n.id.toString(),
          type: 'step', // <--- CLAVE: 'step' hace líneas rectas (tipo organigrama), 'smoothstep' curvas
          style: { stroke: '#525252', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#525252' }, // Flecha al final
        }));

      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(flowNodes, flowEdges);
      
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    } catch (error) {
      console.error("Error loading hierarchy:", error);
    } finally {
      setLoading(false);
    }
  }, [eventId, currentUserId, isCreator, setNodes, setEdges]);

  useEffect(() => {
    loadGraph();
  }, [loadGraph]);

  // --- FUNCIONES MODAL ---
  const openAddModal = (parentId, parentDepth = 0) => {
    setTargetParentId(parentId);
    setTargetParentDepth(parentDepth);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedUserForRole(null);
    setIsModalOpen(true);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) return setSearchResults([]);
    try {
        const res = await fetchWithAuth(`${API_URL}/users/search?query=${query}`);
        const users = Array.isArray(res) ? res : (res.data || []);
        // Filtrar usuarios que ya estén en el grafo para no duplicar (opcional)
        setSearchResults(users);
    } catch (e) {
        console.error(e);
    }
  };

  const handleConfirmAdd = async (depth) => {
    try {
        setIsAdding(true);
        await fetchWithAuth(`${API_URL}/events/${eventId}/collaborators`, {
            method: 'POST',
            body: JSON.stringify({
                username: selectedUserForRole.username, 
                parent_collab_id: targetParentId,
                target_depth: depth
            })
        });
        setIsModalOpen(false);
        setTimeout(() => loadGraph(), 200); 
    } catch (error) {
        alert(error.message || "Error al agregar");
    } finally {
        setIsAdding(false);
    }
  };

  const handleRemoveUser = async (username) => {
    try {
        setLoading(true);
        await fetchWithAuth(`${API_URL}/events/${eventId}/collaborators/${username}`, {
            method: 'DELETE'
        });
        // Recargamos el organigrama
        loadGraph();
    } catch (error) {
        alert(error.message || "Error al retirar al usuario.");
        setLoading(false);
    }
  };

  // --- RENDER ---
  return (
    <div className="w-full h-[85vh] bg-[#050505] rounded-2xl border border-white/10 overflow-hidden relative animate-fade-in shadow-2xl">
        
        {/* Header Flotante */}
        <div className="absolute top-0 left-0 w-full p-6 z-10 flex justify-between items-start pointer-events-none">
            <div>
                <h2 className="text-2xl font-semibold text-white tracking-tight">Organigrama</h2>
                <p className="text-gray-400 text-sm">Gestiona la jerarquía de promotores.</p>
            </div>
        </div>

        {/* Botón Raíz (Solo Admin) */}
        {isCreator && (
            <button 
                onClick={() => openAddModal(null)}
                className="absolute top-6 right-6 z-20 px-4 py-2 bg-white text-black rounded-full text-sm font-bold hover:scale-105 transition-transform flex items-center gap-2 shadow-[0_0_20px_rgba(255,255,255,0.3)] pointer-events-auto"
            >
                <Plus size={16} /> NUEVA CABEZA
            </button>
        )}

        {/* Canvas del Grafo */}
        <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.2}
            maxZoom={1.5}
            className="bg-[#050505]"
            proOptions={{ hideAttribution: true }} // Oculta logo de ReactFlow si tienes licencia, o déjalo
        >
            <Background color="#222" gap={24} size={1} variant="dots" />
            <Controls className="!bg-white/5 !border-white/10 !fill-white" />
        </ReactFlow>

        {/* --- MODAL DE AGREGAR MIEMBRO --- */}
        {isModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-in fade-in zoom-in-95 duration-200">
                <div className="bg-[#111] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
                    <div className="p-5 border-b border-white/10 flex justify-between items-center bg-white/5">
                        <h3 className="text-white font-medium">
                            {!selectedUserForRole 
                                ? (targetParentId ? "Agregar a Equipo" : "Nueva Cabeza de Equipo")
                                : "Asignar Rol"}
                        </h3>
                        <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                    
                    <div className="p-5 flex-1 overflow-hidden flex flex-col">
                        {/* VISTA 1: BUSCADOR */}
                        {!selectedUserForRole ? (
                            <>
                                <div className="relative mb-4">
                                    <Search className="absolute left-3 top-3.5 text-gray-500" size={18} />
                                    <input 
                                        type="text"
                                        autoFocus
                                        value={searchQuery}
                                        onChange={(e) => handleSearch(e.target.value)}
                                        placeholder="Buscar usuario..."
                                        className="w-full bg-[#050505] border border-white/20 rounded-xl py-3 pl-10 pr-4 text-white focus:outline-none focus:border-white/50 transition-colors placeholder-gray-600"
                                    />
                                </div>

                                <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                                    {searchResults.map((user) => (
                                        <button
                                            key={user.id || user.username}
                                            onClick={() => setSelectedUserForRole(user)} // Cambiamos a la Vista 2
                                            className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 group transition-colors text-left border border-transparent hover:border-white/10"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gray-800 border border-white/5 flex items-center justify-center text-xs text-white overflow-hidden">
                                                    {user.profile_image_url ? (
                                                        <img src={getImageUrl(user.profile_image_url)} alt="" className="w-full h-full object-cover"/>
                                                    ) : <User size={16}/>}
                                                </div>
                                                <div>
                                                    <p className="text-white text-sm font-medium">@{user.username}</p>
                                                    <p className="text-xs text-gray-500">{user.name}</p>
                                                </div>
                                            </div>
                                            <div className="w-8 h-8 rounded-full bg-white text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:scale-110">
                                                <Plus size={16} />
                                            </div>
                                        </button>
                                    ))}
                                    {searchQuery.length > 1 && searchResults.length === 0 && (
                                        <div className="text-center py-8 text-gray-500 text-sm">No encontramos a ese usuario.</div>
                                    )}
                                </div>
                            </>
                        ) : (
                            /* VISTA 2: SELECCIÓN DE ROL */
                            <div className="flex-1 flex flex-col space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                                <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10 mb-2">
                                    <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center text-white overflow-hidden shrink-0">
                                        {selectedUserForRole.profile_image_url ? (
                                            <img src={getImageUrl(selectedUserForRole.profile_image_url)} className="w-full h-full object-cover" alt="" />
                                        ) : <User size={20}/>}
                                    </div>
                                    <div>
                                        <p className="text-white font-medium">@{selectedUserForRole.username}</p>
                                        <p className="text-gray-400 text-xs">Selecciona el rol que ocupará en tu equipo:</p>
                                    </div>
                                </div>

                                <div className="space-y-2 flex-1">
                                    {/* Filtramos para solo mostrar roles con profundidad mayor al padre actual */}
                                    {[
                                        { depth: 1, label: "Socio", desc: "Nivel Máximo" },
                                        { depth: 2, label: "Coordinador", desc: "Gestiona cabezas de equipo" },
                                        { depth: 3, label: "Cabeza de Equipo", desc: "Gestiona promotores" },
                                        { depth: 4, label: "Promotor", desc: "Nivel Base" }
                                    ].filter(role => role.depth > targetParentDepth).map(role => (
                                        <button
                                            key={role.depth}
                                            onClick={() => handleConfirmAdd(role.depth)}
                                            disabled={isAdding}
                                            className="w-full text-left p-4 rounded-xl border border-white/10 bg-black/20 hover:bg-white/10 transition-all flex items-center justify-between group disabled:opacity-50"
                                        >
                                            <div>
                                                <p className="text-white font-medium">{role.label}</p>
                                                <p className="text-gray-500 text-xs">{role.desc}</p>
                                            </div>
                                            <div className="text-gray-500 group-hover:text-white group-hover:translate-x-1 transition-all">
                                                →
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                <button 
                                    onClick={() => setSelectedUserForRole(null)}
                                    className="mt-4 p-3 text-center w-full text-gray-400 hover:text-white text-sm hover:bg-white/5 rounded-xl transition-colors"
                                >
                                    ← Volver a buscar
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};

export default EventHierarchy;